import React, { useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    useGetAdminNewsItemQuery,
    useUpdateAdminNewsMutation,
    useGetAdminNewsCategoriesQuery,
    useUploadAdminNewsImageMutation,
} from "../store/api";
import { UpdateAdminNewsRequestBody } from "../api/news";
import NewsEditor from "../components/news/NewsEditor";
import { useNotification } from "../hooks/useNotification";
import Notification from "../components/ui/Notification";

const AdminNewsEditPage: React.FC = () => {
    const { id } = useParams();
    const newsId = Number(id);
    const navigate = useNavigate();

    const { data: detail, isFetching } = useGetAdminNewsItemQuery(newsId);
    const [updateNews, { isLoading: isSaving }] = useUpdateAdminNewsMutation();
    const { data: categories } = useGetAdminNewsCategoriesQuery();
    const [uploadImage] = useUploadAdminNewsImageMutation();
    const { showError, showSuccess, notification, hideNotification } = useNotification();

    const initialTitles = useMemo(
        () =>
            detail?.translations
                ? Object.fromEntries(
                      Object.entries(detail.translations).map(([l, t]: any) => [
                          l,
                          t?.title || "",
                      ])
                  )
                : { ru: "" },
        [detail]
    );
    const initialBody = useMemo(() => {
        if (!detail?.translations) return { ru: "" };
        return Object.fromEntries(
            Object.entries(detail.translations).map(([l, t]: any) => [
                l,
                t?.body_html || "",
            ])
        );
    }, [detail]);
    const initialExcerpt = useMemo(() => {
        if (!detail?.translations) return { ru: "" };
        return Object.fromEntries(
            Object.entries(detail.translations).map(([l, t]: any) => [
                l,
                t?.excerpt || "",
            ])
        );
    }, [detail]);
    const initialSeoTitle = useMemo(() => {
        if (!detail?.translations) return { ru: "" };
        return Object.fromEntries(
            Object.entries(detail.translations).map(([l, t]: any) => [
                l,
                t?.seo_title || "",
            ])
        );
    }, [detail]);
    const initialSeoDescription = useMemo(() => {
        if (!detail?.translations) return { ru: "" };
        return Object.fromEntries(
            Object.entries(detail.translations).map(([l, t]: any) => [
                l,
                t?.seo_description || "",
            ])
        );
    }, [detail]);
    const initialSeoKeywords = useMemo(() => {
        if (!detail?.translations) return { ru: "" };
        return Object.fromEntries(
            Object.entries(detail.translations).map(([l, t]: any) => [
                l,
                t?.seo_keywords || "",
            ])
        );
    }, [detail]);

    const languages = useMemo(
        () =>
            Object.keys({
                ru: "",
                en: "",
                ka: "",
                uk: "",
                ...initialTitles,
            }),
        [initialTitles]
    );

    const [activeLang, setActiveLang] = useState<string>(languages[0] || "ru");
    const [titles, setTitles] = useState<Record<string, string>>(initialTitles);
    const [videoUrl, setVideoUrl] = useState<string>(
        detail?.alny_news_video_url || ""
    );
    const [slug, setSlug] = useState<string>(detail?.slug || "");
    const [newsCategoryId, setNewsCategoryId] = useState<number | "">(
        detail?.news_category_id || ""
    );
    const [isPublished, setIsPublished] = useState<boolean>(
        typeof detail?.is_published === "boolean" ? detail.is_published : true
    );
    const [showPoster, setShowPoster] = useState<boolean>(
        typeof detail?.show_poster === "boolean" ? detail.show_poster : false
    );
    const [excerpt, setExcerpt] =
        useState<Record<string, string>>(initialExcerpt);
    const [bodyHtml, setBodyHtml] = useState<Record<string, string>>(initialBody);
    const [seoTitle, setSeoTitle] =
        useState<Record<string, string>>(initialSeoTitle);
    const [seoDescription, setSeoDescription] =
        useState<Record<string, string>>(initialSeoDescription);
    const [seoKeywords, setSeoKeywords] =
        useState<Record<string, string>>(initialSeoKeywords);
    const [posterFile, setPosterFile] = useState<File | null>(null);
    const [posterPreview, setPosterPreview] = useState<string | null>(
        detail?.poster || null
    );

    React.useEffect(() => {
        if (detail) {
            setVideoUrl(detail?.alny_news_video_url || "");
            setSlug(detail?.slug || "");
            setNewsCategoryId(detail?.news_category_id || "");
            setIsPublished(
                typeof detail?.is_published === "boolean" ? detail.is_published : true
            );
            setShowPoster(
                typeof detail?.show_poster === "boolean" ? detail.show_poster : false
            );
            setPosterPreview(detail?.poster || null);

            const trs = detail?.translations || {};
            const tMap: Record<string, string> = {};
            const eMap: Record<string, string> = {};
            const bMap: Record<string, string> = {};
            const stMap: Record<string, string> = {};
            const sdMap: Record<string, string> = {};
            const skMap: Record<string, string> = {};
            Object.entries(trs).forEach(([l, t]: any) => {
                tMap[l] = t?.title || "";
                eMap[l] = t?.excerpt || "";
                bMap[l] = t?.body_html || "";
                stMap[l] = t?.seo_title || "";
                sdMap[l] = t?.seo_description || "";
                skMap[l] = t?.seo_keywords || "";
            });
            setTitles(tMap);
            setExcerpt(eMap);
            setBodyHtml(bMap);
            setSeoTitle(stMap);
            setSeoDescription(sdMap);
            setSeoKeywords(skMap);

            const langs = Object.keys({ ru: "", en: "", ka: "", uk: "", ...tMap });
            setActiveLang(langs[0] || "ru");
        }
    }, [detail]);

    const handleTitleChange = (value: string) => {
        setTitles((prev) => ({ ...prev, [activeLang]: value }));
    };

    const handleUploadImage = async (file: File) => {
        const resp = await uploadImage(file).unwrap();
        return resp.url;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            let payload: UpdateAdminNewsRequestBody;

            const translations = Object.fromEntries(
                languages.map((l) => [
                    l,
                    {
                        title: titles[l] || "",
                        excerpt: excerpt[l] || null,
                        body_html: bodyHtml[l] || "",
                        seo_title: seoTitle[l] || null,
                        seo_description: seoDescription[l] || null,
                        seo_keywords: seoKeywords[l] || null,
                    },
                ])
            );

            if (posterFile) {
                // Используем FormData для отправки файла
                payload = new FormData();
                payload.append("translations", JSON.stringify(translations));
                if (slug) payload.append("slug", slug);
                if (newsCategoryId) {
                    payload.append("news_category_id", String(newsCategoryId));
                }
                payload.append("is_published", isPublished ? "1" : "0");
                payload.append("show_poster", showPoster ? "1" : "0");
                if (videoUrl) payload.append("alny_news_video_url", videoUrl);
                payload.append("alny_news_poster", posterFile);
            } else {
                // Обычная JSON отправка
                payload = {
                    slug: slug || null,
                    news_category_id: newsCategoryId ? Number(newsCategoryId) : null,
                    is_published: isPublished,
                    show_poster: showPoster,
                    alny_news_video_url: videoUrl || null,
                    translations,
                };
            }

            await updateNews({ id: newsId, data: payload }).unwrap();
            showSuccess("Новость успешно обновлена");
            setTimeout(() => {
                navigate("/dashboard/news");
            }, 1000);
        } catch (err: any) {
            console.error(err);

            // Обработка ошибок валидации
            if (err?.data?.errors) {
                const validationErrors = err.data.errors;
                const errorMessages: string[] = [];

                // Собираем все ошибки валидации
                Object.keys(validationErrors).forEach((field) => {
                    const fieldErrors = validationErrors[field];
                    if (Array.isArray(fieldErrors)) {
                        fieldErrors.forEach((error: string) => {
                            errorMessages.push(`${field}: ${error}`);
                        });
                    } else if (typeof fieldErrors === "string") {
                        errorMessages.push(`${field}: ${fieldErrors}`);
                    }
                });

                if (errorMessages.length > 0) {
                    showError(errorMessages.join("\n"));
                } else {
                    showError("Ошибка валидации данных");
                }
            } else if (err?.data?.message) {
                showError(err.data.message);
            } else if (err?.message) {
                showError(err.message);
            } else {
                showError("Произошла ошибка при обновлении новости");
            }
        }
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-semibold">
                    Редактирование новости #{newsId}
                </h1>
                <button
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded"
                    onClick={() => navigate(-1)}
                >
                    Назад
                </button>
            </div>

            {isFetching && (
                <div className="py-10 text-center text-gray-500 dark:text-gray-400">
                    Загрузка...
                </div>
            )}

            {detail && (
                <form className="space-y-6" onSubmit={handleSubmit}>
                    <div>
                        <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                            <nav
                                className="-mb-px flex space-x-4"
                                aria-label="Tabs"
                            >
                                {languages.map((lng) => (
                                    <button
                                        key={lng}
                                        type="button"
                                        onClick={() => setActiveLang(lng)}
                                        className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${
                                            activeLang === lng
                                                ? "border-blue-600 text-blue-600"
                                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                        }`}
                                    >
                                        {lng.toUpperCase()}
                                    </button>
                                ))}
                            </nav>
                        </div>

                        <div className="grid gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Заголовок ({activeLang})
                                </label>
                                <input
                                    type="text"
                                    className="w-full border rounded px-3 py-2"
                                    value={titles[activeLang] || ""}
                                    onChange={(e) =>
                                        handleTitleChange(e.target.value)
                                    }
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Короткое описание ({activeLang})
                                </label>
                                <textarea
                                    className="w-full border rounded px-3 py-2 h-24"
                                    value={excerpt[activeLang] || ""}
                                    onChange={(e) =>
                                        setExcerpt((prev) => ({
                                            ...prev,
                                            [activeLang]: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Текст (редактор) ({activeLang})
                                </label>
                                <NewsEditor
                                    value={bodyHtml[activeLang] || ""}
                                    onChange={(html) =>
                                        setBodyHtml((prev) => ({
                                            ...prev,
                                            [activeLang]: html,
                                        }))
                                    }
                                    onUploadImage={handleUploadImage}
                                />
                            </div>
                            <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        SEO title ({activeLang})
                                    </label>
                                    <input
                                        className="w-full border rounded px-3 py-2"
                                        value={seoTitle[activeLang] || ""}
                                        onChange={(e) =>
                                            setSeoTitle((prev) => ({
                                                ...prev,
                                                [activeLang]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        SEO description ({activeLang})
                                    </label>
                                    <input
                                        className="w-full border rounded px-3 py-2"
                                        value={seoDescription[activeLang] || ""}
                                        onChange={(e) =>
                                            setSeoDescription((prev) => ({
                                                ...prev,
                                                [activeLang]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        SEO keywords ({activeLang})
                                    </label>
                                    <input
                                        className="w-full border rounded px-3 py-2"
                                        value={seoKeywords[activeLang] || ""}
                                        onChange={(e) =>
                                            setSeoKeywords((prev) => ({
                                                ...prev,
                                                [activeLang]: e.target.value,
                                            }))
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Slug (опционально)
                            </label>
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                value={slug}
                                onChange={(e) => setSlug(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Категория
                            </label>
                            <select
                                className="w-full border rounded px-3 py-2"
                                value={newsCategoryId}
                                onChange={(e) =>
                                    setNewsCategoryId(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : ""
                                    )
                                }
                            >
                                <option value="">—</option>
                                {Array.isArray(categories) &&
                                    categories.map((c: any) => (
                                        <option key={c.id} value={c.id}>
                                            {c.translations?.ru?.name || c.slug}
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                URL видео
                            </label>
                            <input
                                type="text"
                                className="w-full border rounded px-3 py-2"
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                            />
                        </div>
                        <label className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                className="h-4 w-4"
                                checked={isPublished}
                                onChange={(e) =>
                                    setIsPublished(e.target.checked)
                                }
                            />
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                                Опубликовано
                            </span>
                        </label>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Постер (до 5MB)
                            </label>
                            {posterPreview && (
                                <div className="mb-2">
                                    <img
                                        src={posterPreview}
                                        alt="Текущий постер"
                                        className="max-w-xs max-h-48 object-contain border rounded"
                                    />
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0] || null;
                                    setPosterFile(file);
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                            setPosterPreview(
                                                reader.result as string
                                            );
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                            />
                            <label className="flex items-center space-x-3 mt-2">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4"
                                    checked={showPoster}
                                    onChange={(e) =>
                                        setShowPoster(e.target.checked)
                                    }
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Показывать постер
                                </span>
                            </label>
                        </div>
                    </div>

                    <div className="flex items-center space-x-3">
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                            disabled={isSaving}
                        >
                            Сохранить
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded"
                            onClick={() => navigate("/dashboard/news")}
                        >
                            Отмена
                        </button>
                    </div>
                </form>
            )}

            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
        </div>
    );
};

export default AdminNewsEditPage;
